import Button from '@dustin-web/microsite-components/src/button'
import { useSiteContext, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { InputType, TextInput } from '@dustin-web/microsite-components/src/text-input'
import { getCultureCode, getDefaultLanguage } from '@dustin-web/microsite-config'
import { useGraphqlMutation } from '@dustin-web/microsite-data/src/hooks'
import { ProductDigitalData } from '@dustin-web/product-components/src/utils/product-digital-data'
import { ProductWithCategoryType } from '@dustin-web/product-components/src/utils/digital-data-types'
import { getUniqueFavoriteListName } from '@dustin-web/product-components/src/utils/favorite-list-name-generator'
import { ChangeEvent, FormEventHandler, useCallback, useState } from 'react'
import { CreateFavoriteListFromCart } from '@cartsite-app/src/graphql/create-favorite-list-from-cart.graphql'
import {
  CreateFavoriteListFromCartMutation,
  CreateFavoriteListFromCartMutationVariables,
} from '@cartsite-app/src/graphql/generated/graphql-types'
import { useExtendedFavorites } from '../favorites/use-extended-favorites'

type Props = {
  productsInCart: ProductWithCategoryType[]
  loading: boolean
  setLoading: () => void
}

export const CreateFavoriteListDialog = ({ productsInCart, loading, setLoading }: Props) => {
  const { translate } = useTranslations()
  const { favoritesData } = useExtendedFavorites()
  const { site } = useSiteContext()
  const defaultLanguage = getDefaultLanguage(site)
  const [favoriteListName, setFavoriteListName] = useState('')
  const [listNameError, setListNameError] = useState(false)

  const dateString = new Date().toLocaleDateString(getCultureCode(defaultLanguage), {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  })
  const defaultFavoriteListName = `${translate('Cart_FavoriteListName')} ${dateString}`

  const [createFavoriteListFromCart] = useGraphqlMutation<
    CreateFavoriteListFromCartMutation,
    CreateFavoriteListFromCartMutationVariables
  >(CreateFavoriteListFromCart)

  const listNames = [...favoritesData].map(list =>
    list?.productListName ? list.productListName : ''
  )

  const onChangeFavoriteListName = (event: ChangeEvent<HTMLInputElement>) => {
    setFavoriteListName(event.target.value)
    setListNameError(false)
  }

  const onSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async event => {
      event.preventDefault()

      if (loading) {
        return
      }

      setLoading()

      ProductDigitalData.pushAddToWishList(productsInCart, 'shoppingCartPage')

      const listName = getUniqueFavoriteListName(
        favoriteListName !== '' ? favoriteListName : defaultFavoriteListName,
        listNames
      )
      const result = await createFavoriteListFromCart({
        variables: {
          favoriteListName: listName,
        },
      })

      const newId = result.createFavoriteListFromCart?.id
      window.location.href = `/favorites?listId=${newId}`
    },
    [loading, listNames, favoriteListName]
  )

  return (
    <>
      <form className="c-form c-form--dark row" onSubmit={onSubmit}>
        <div className="col-8">
          <TextInput
            type={InputType.TEXT}
            className="u-mb-0"
            label={translate('Favorites_CreateNewFavoriteList')}
            placeHolder={defaultFavoriteListName}
            value={favoriteListName}
            autoFocus={true}
            onChange={onChangeFavoriteListName}
            error={listNameError}
            customErrorText={translate('Favorites_NameExists')}
          />
        </div>
        <div className="col-4 d-flex align-items-end">
          <Button
            type="submit"
            disabled={loading}
            className="u-mb-0 c-btn--block c-btn--shrink"
            text={translate('Create')}
            primary={true}
          />
        </div>
      </form>
    </>
  )
}
